import {
    Grid,
    IconButton,
    IconButtonProps,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    Forward30,
    Replay30,
    SkipNext,
    SkipPrevious,
} from "@mui/icons-material";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import AcxButton, { IAcxButtonProps } from "components/UI/AcxButton";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useEffect } from "react";
import { isUndefinedType } from "../../../utils/TypeGuards";
import SoundClipEditorStore from "../Stores/SoundClipEditorStore";
import PlayControl, { PlayStatus } from "./PlayControl";
import PlaybackRate from "./PlaybackRate";
import VolumeSlider from "./VolumeSlider";
import Zoom from "./Zoom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { useStore } from "utils/useStore";
import { ConversationService } from "services/ConversationService";
import { EvalStore } from "components/Evaluation/Stores/EvalStore";
import { AuthStore } from "stores/AuthStore";

interface IPlayerV2Props {
    enablePlaybackRate?: boolean;
    viewModel: SoundClipEditorStore;
    enableZoom?: boolean;
    iconButton?: React.ReactNode;
    iconButtonProps?: Partial<IconButtonProps>;
    textButton?: string | null;
    textButtonProps?: Partial<IAcxButtonProps>;
    onPressPlay?: () => void;
    onPressPause?: () => void;
}

const Player: React.FC<IPlayerV2Props> = observer(
    ({
        enablePlaybackRate,
        viewModel,
        enableZoom,
        iconButton,
        iconButtonProps = {},
        textButton,
        textButtonProps = {},
        onPressPlay,
        onPressPause,
    }) => {
        const theme = useTheme();
        const [playStatus, setPlayStatus] = React.useState<PlayStatus>(
            PlayStatus.Stopped,
        );

        const [hasVideo, setHasVideo] = React.useState(false);

        const conversationService = new ConversationService();

        const evalStore = useStore(EvalStore);
        const authStore = useStore(AuthStore);

        const canDownload =
            Boolean(viewModel.clipAudioBuff) || viewModel.isStreamingMode;

        const playAudio = () => {
            setPlayStatus(PlayStatus.Play);
            viewModel.onPlayClick();
            if (onPressPlay) {
                onPressPlay();
            }
        };

        const pauseAudio = () => {
            setPlayStatus(PlayStatus.Paused);
            viewModel.onPlayClick();
            if (onPressPause) {
                onPressPause();
            }
        };

        const getDurationDisplay = () => {
            const audioDuration = viewModel.audioDuration;
            if (audioDuration === undefined) {
                return "";
            }

            const minutes = Math.floor(audioDuration / 60);
            const seconds = Math.floor(audioDuration % 60);

            return ` (${minutes}:${seconds})`;
        };

        useEffect(() => {
            const status = viewModel.isPlaying
                ? PlayStatus.Play
                : PlayStatus.Paused;
            setPlayStatus(status);
        }, [viewModel.isPlaying]);

        const amdId = evalStore.currentEval?.interaction?.audioMetadataId;

        useEffect(() => {
            if (amdId && evalStore.authStore.canUserView("Screen Recording")) {
                try {
                    conversationService
                        .getScreenRecordings(amdId)
                        .then((res) => {
                            if (res && Object.keys(res).length) {
                                setHasVideo(true);
                            } else {
                                setHasVideo(false);
                            }
                        });
                } catch (e) {
                    // Don't log message to user
                    setHasVideo(false);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [amdId]);

        return (
            <>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-around"
                    alignItems="center"
                    id="main-grid"
                    sx={{
                        padding: "12px 22px",
                        height: "100%",
                        backgroundColor: "#FAFCFF",
                    }}
                >
                    <Grid
                        container
                        item
                        xs={4}
                        justifyContent="flex-start"
                        alignItems="center"
                    >
                        {evalStore.authStore.canUserView("Screen Recording") &&
                            hasVideo && (
                                <Grid item>
                                    <Link
                                        className="pendo-ignore"
                                        to={"/focus/video/" + amdId}
                                        target="_blank"
                                        style={{ textDecoration: "none" }}
                                    >
                                        <AcxButton
                                            fullWidth
                                            color="white"
                                            startIcon={<OpenInNewIcon />}
                                        >
                                            View Video
                                        </AcxButton>
                                    </Link>
                                </Grid>
                            )}
                        {iconButton && (
                            <IconButton
                                sx={{ color: theme.palette.blue.main }}
                                {...iconButtonProps}
                                size="large"
                            >
                                {iconButton}
                            </IconButton>
                        )}
                        <VolumeSlider
                            onVolumeChange={viewModel.changeVolume}
                            onChannel1Change={viewModel.changeChannel1Volume}
                            onChannel2Change={viewModel.changeChannel2Volume}
                            multiChannel={viewModel.isMultiChannel}
                        />
                        {(authStore.isUserUltra() ||
                            authStore.canUserView("Download Audio")) && (
                            <Tooltip title="Download">
                                <span>
                                    <IconButton
                                        sx={{
                                            color: theme.palette.blue.main,
                                        }}
                                        onClick={viewModel.downloadCurrentClip}
                                        disabled={!canDownload}
                                        size="large"
                                    >
                                        <DownloadRounded fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Grid>
                    <Grid
                        container
                        item
                        xs={4}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Grid item>
                            <Tooltip title="Go to beginning">
                                <IconButton
                                    sx={{ padding: 1 }}
                                    onClick={viewModel.goToBegin}
                                    size={"medium"}
                                >
                                    <SkipPrevious
                                        sx={{ color: theme.palette.blue.main }}
                                        fontSize={
                                            useMediaQuery(
                                                theme.breakpoints.down("lg"),
                                            )
                                                ? "small"
                                                : "large"
                                        }
                                        color={"inherit"}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title="Go back 30 seconds">
                                <IconButton
                                    sx={{ padding: 1 }}
                                    onClick={viewModel.goBack30}
                                    size={"medium"}
                                >
                                    <Replay30
                                        sx={{ color: theme.palette.blue.main }}
                                        fontSize={
                                            useMediaQuery(
                                                theme.breakpoints.down("lg"),
                                            )
                                                ? "small"
                                                : "large"
                                        }
                                        color={"inherit"}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <PlayControl
                                style={{ color: "#3564D5" }}
                                play={playAudio}
                                pause={pauseAudio}
                                status={playStatus}
                                color={"inherit"}
                            />
                        </Grid>
                        <Grid item>
                            <Tooltip title="Go forward 30 seconds">
                                <IconButton
                                    sx={{ padding: 1 }}
                                    onClick={viewModel.goForward30}
                                    size={"medium"}
                                >
                                    <Forward30
                                        color={"inherit"}
                                        sx={{ color: theme.palette.blue.main }}
                                        fontSize={
                                            useMediaQuery(
                                                theme.breakpoints.down("lg"),
                                            )
                                                ? "small"
                                                : "large"
                                        }
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title={"Go to end" + getDurationDisplay()}>
                                <IconButton
                                    sx={{ padding: 1 }}
                                    onClick={viewModel.goToEnd}
                                    size={"medium"}
                                >
                                    <SkipNext
                                        color={"inherit"}
                                        sx={{ color: "#3564D5" }}
                                        fontSize={
                                            useMediaQuery(
                                                theme.breakpoints.down("lg"),
                                            )
                                                ? "small"
                                                : "large"
                                        }
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        item
                        xs={4}
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        {enableZoom && <Zoom viewModel={viewModel} />}
                        {durationTitleDisplay(
                            viewModel.playHeadTime,
                            viewModel.audioDuration,
                        ) &&
                            enablePlaybackRate && (
                                <Grid
                                    item
                                    sx={{
                                        margin: "0 0.5rem",
                                        padding: "0 0.25rem",
                                        borderRadius: "0.25rem",
                                    }}
                                >
                                    <PlaybackRate viewModel={viewModel} />
                                </Grid>
                            )}
                        <Grid item>
                            <Typography
                                sx={{
                                    color: theme.palette.blue.main,
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    paddingTop: "2px",
                                }}
                            >
                                {durationTitleDisplay(
                                    viewModel.playHeadTime,
                                    viewModel.audioDuration,
                                )}
                            </Typography>
                        </Grid>
                        {textButton && (
                            <Grid item>
                                <AcxButton
                                    fullWidth
                                    style={{
                                        marginRight: 0,
                                        marginLeft: "16px",
                                    }}
                                    color="white"
                                    {...textButtonProps}
                                >
                                    {textButton}
                                </AcxButton>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </>
        );
    },
);

export function durationDisplay(val: number) {
    const dur = moment.duration(val, "seconds");

    const minutesAndSeconds = `${`${dur.minutes()}`.padStart(
        2,
        "0",
    )}:${`${dur.seconds()}`.padStart(2, "0")}`;

    if (dur.hours()) {
        return `${dur.hours()}:` + minutesAndSeconds;
    } else {
        return minutesAndSeconds;
    }
}

export function durationTitleDisplay(
    currentTime: number | undefined,
    duration: number | undefined,
) {
    if (!isUndefinedType(duration) && !isUndefinedType(currentTime)) {
        return `${durationDisplay(currentTime)} / ${durationDisplay(duration)}`;
    } else {
        return undefined;
    }
}

export default Player;
